/**
 * Profile editing — reachable whenever, not just at onboarding. Avatar lives
 * in the shared AvatarEditor; this screen adds display name + bio editing
 * (the handle is your address and never changes). Saves go through
 * PATCH /api/profiles/me; relationship caches are invalidated so peers see the
 * new name/bio immediately too.
 */

import { useCallback, useEffect, useState } from 'react';
import { ArrowLeft, AlertCircle, Check, Mail, Share2 } from 'lucide-react';
import { useAppDispatch } from '../../store/hooks';
import { relationshipsApi, useMineQuery } from '../relationships/api';
import { useInboxQuery, useOutboxQuery } from '../letters/api';
import { TabBar } from '../../TabBar';
import { useMeQuery, useUpdateProfileMutation } from './api';
import { useChangePasswordMutation, useGetSessionQuery } from '../auth/api';
import { Avatar } from './avatar';
import { AvatarEditor } from './AvatarEditor';
import { ProfileOrbit } from './ProfileOrbit';
import { SecretCodeCard } from '../hidden/HiddenUI';
import { SetAsideSection } from '../hidden/SetAsideUI';
import { SignatureCard } from '../personal/Signature';
import { ShareKitSheet } from '../onboarding/ShareKit';
import { useResendVerification } from '../onboarding/verify';
import { track } from '../onboarding/track';


export function ProfileScreen({ onBack }: { onBack: () => void }) {
  const { data: me } = useMeQuery();
  const { data: sessionUser } = useGetSessionQuery();
  const [updateProfile, { isLoading: saving }] = useUpdateProfileMutation();
  const [changePassword, { isLoading: changingPassword }] = useChangePasswordMutation();
  const [displayName, setDisplayName] = useState('');
  const [sharing, setSharing] = useState(false);
  const [bio, setBio] = useState('');
  const [birthday, setBirthday] = useState('');
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [pwError, setPwError] = useState<string | null>(null);
  const [pwChanged, setPwChanged] = useState(false);
  const dispatch = useAppDispatch();
  const { data: relationships } = useMineQuery();
  const { data: inbox } = useInboxQuery();
  const { data: outbox } = useOutboxQuery();
  const correspondents = (relationships ?? []).filter((r) => r.status === 'accepted').length;

  useEffect(() => {
    if (me) {
      setDisplayName(me.displayName);
      setBio(me.bio ?? '');
      setBirthday(me.birthday ?? '');
      setDirty(false);
    }
  }, [me]);

  const onSave = useCallback(async () => {
    setError(null);
    if (!me) return;
    const patch: { displayName?: string; bio?: string; birthday?: string | null } = {};
    const name = displayName.trim();
    if (name && name !== me.displayName) patch.displayName = name;
    const b = bio.trim();
    if (b !== (me.bio ?? '')) patch.bio = b;
    // An emptied date field is a clear, not a date that failed to parse.
    if (birthday !== (me.birthday ?? '')) patch.birthday = birthday || null;
    if (Object.keys(patch).length === 0) {
      setDirty(false);
      return;
    }
    try {
      await updateProfile(patch).unwrap();
      if (patch.bio !== undefined) track({ event: 'bio_set' });
      // Peers' names/bios appear in the conversation list cache — refresh it.
      dispatch(relationshipsApi.util.invalidateTags(['Relationship']));
      setDirty(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save your profile');
    }
  }, [me, displayName, bio, birthday, updateProfile, dispatch]);

  if (!me) return null;

  // The registered address comes with the profile; the session is a fallback.
  const email = me.email || sessionUser?.email || '';

  const onChangePassword = useCallback(async () => {
    setPwError(null);
    setPwChanged(false);
    if (newPassword.length < 8) {
      setPwError('Passwords are at least 8 characters long');
      return;
    }
    if (newPassword !== confirmNewPassword) {
      setPwError('Those passwords do not match');
      return;
    }
    try {
      await changePassword({ currentPassword, newPassword }).unwrap();
      setPwChanged(true);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
    } catch (e) {
      const err = e as { data?: { message?: string | string[] }; status?: unknown };
      const message = Array.isArray(err.data?.message) ? err.data!.message[0] : err.data?.message;
      setPwError(message === 'Invalid password' ? 'Your current password is wrong' : message ?? 'Could not change your password');
    }
  }, [currentPassword, newPassword, confirmNewPassword, changePassword]);

  return (
    <section className="screen screen--tabbed" aria-label="Your profile">
      <header className="screen__header">
        <button type="button" className="screen__back" onClick={onBack} aria-label="Back">
          <ArrowLeft size={20} />
        </button>
        <span className="screen__title">Your profile</span>
      </header>

      <div className="profile-screen">
        <section className="profile-card" aria-label="Profile summary">
          <ProfileOrbit relationships={relationships ?? []}>
            <span className="profile-card__photo">
              <Avatar name={me.displayName} avatar={me.avatar} />
            </span>
          </ProfileOrbit>
          <span className="profile-card__name">{me.displayName}</span>
          <span className="profile-card__handle">@{me.handle}</span>
          {email && (
            <span className="profile-card__email">
              <Mail size={13} aria-hidden="true" />
              {email}
            </span>
          )}
          {me.bio && <p className="profile-card__bio">{me.bio}</p>}
          <span className="profile-card__stats">
            <span className="stat-chip">
              <strong>{correspondents}</strong> {correspondents === 1 ? 'friend' : 'friends'}
            </span>
            <span className="stat-chip">
              <strong>{outbox?.length ?? 0}</strong> sent
            </span>
            <span className="stat-chip">
              <strong>{inbox?.length ?? 0}</strong> received
            </span>
          </span>
          <button type="button" className="btn btn--primary profile-card__share" onClick={() => setSharing(true)}>
            <Share2 size={15} aria-hidden="true" /> Share your toiyp
          </button>
        </section>
        {sharing && <ShareKitSheet from="profile" onClose={() => setSharing(false)} />}
        {me.verificationPending && <ConfirmEmailNote email={me.email} />}

        <AvatarEditor displayName={me.displayName} avatar={me.avatar} />

        <section className="profile-fields" aria-label="Profile details">
          <label className="field">
            <span className="field__label">Display name</span>
            <input
              className="field__input"
              value={displayName}
              maxLength={80}
              onChange={(e) => {
                setDisplayName(e.target.value);
                setDirty(true);
              }}
              placeholder="How people see you"
            />
          </label>

          <label className="field">
            <span className="field__label">Bio</span>
            <textarea
              className="form-textarea"
              rows={3}
              value={bio}
              maxLength={280}
              onChange={(e) => {
                setBio(e.target.value);
                setDirty(true);
              }}
              placeholder="A line about you…"
            />
          </label>

          <label className="field">
            <span className="field__label">Birthday</span>
            <input
              className="field__input"
              type="date"
              value={birthday}
              max={new Date().toISOString().slice(0, 10)}
              onChange={(e) => {
                setBirthday(e.target.value);
                setDirty(true);
              }}
            />
          </label>

          <p className="field__hint">
            People you correspond with see the day and the month, so they know
            when to write — never the year.
          </p>

          <p className="field__hint">
            @{me.handle} is your address on the network — it can&apos;t change.
          </p>

          {error && (
            <p className="form-error" role="alert">
              <AlertCircle size={13} aria-hidden="true" /> {error}
            </p>
          )}

          <div className="profile-actions">
            <button
              type="button"
              className="btn btn--primary btn--block"
              disabled={saving || !dirty}
              onClick={() => void onSave()}
            >
              {saving ? 'Saving…' : 'Save profile'}
            </button>
          </div>
        </section>

        <SignatureCard signature={me.signature} handle={me.handle} />

        <section className="profile-fields" aria-label="Change password">
          <h2 className="profile-fields__title">Password</h2>

          <label className="field">
            <span className="field__label">Current password</span>
            <input
              className="field__input"
              type="password"
              value={currentPassword}
              autoComplete="current-password"
              onChange={(e) => {
                setCurrentPassword(e.target.value);
                setPwChanged(false);
              }}
              placeholder="your current password"
            />
          </label>

          <label className="field">
            <span className="field__label">New password</span>
            <input
              className="field__input"
              type="password"
              value={newPassword}
              autoComplete="new-password"
              onChange={(e) => {
                setNewPassword(e.target.value);
                setPwChanged(false);
              }}
              placeholder="at least 8 characters"
            />
          </label>

          <label className="field">
            <span className="field__label">Confirm new password</span>
            <input
              className="field__input"
              type="password"
              value={confirmNewPassword}
              autoComplete="new-password"
              onChange={(e) => {
                setConfirmNewPassword(e.target.value);
                setPwChanged(false);
              }}
              placeholder="again, for good measure"
            />
          </label>

          {pwChanged && (
            <p className="form-success" role="status">
              <Check size={13} aria-hidden="true" /> Your password is updated.
            </p>
          )}
          {pwError && (
            <p className="form-error" role="alert">
              <AlertCircle size={13} aria-hidden="true" /> {pwError}
            </p>
          )}

          <div className="profile-actions">
            <button
              type="button"
              className="btn btn--primary btn--block"
              disabled={changingPassword || !currentPassword || !newPassword}
              onClick={() => void onChangePassword()}
            >
              {changingPassword ? 'Saving…' : 'Change password'}
            </button>
          </div>
        </section>

        <SecretCodeCard />
        <SetAsideSection />
      </div>
      <TabBar active="profile" onLetters={onBack} onProfile={() => {}} />
    </section>
  );
}
/**
 * A gentle note while a verification email is outstanding (slice 4). Only
 * accounts that were sent one ever see it; nothing is locked behind it.
 */
function ConfirmEmailNote({ email }: { email: string }) {
  const resend = useResendVerification();
  return (
    <section className="confirm-email" aria-label="Confirm your email">
      <Mail size={16} aria-hidden="true" className="confirm-email__icon" />
      <div>
        <p className="confirm-email__title">Confirm your email</p>
        <p className="confirm-email__body">
          We sent a link to {email}. Confirming it means that when someone you invited writes their
          first letter, you both get a welcome stamp.
        </p>
        <button type="button" className="linkish" disabled={resend.sending} onClick={() => void resend.send()}>
          {resend.sending ? 'Sending…' : 'Send the link again'}
        </button>
        {resend.message && (
          <p className="confirm-email__body" role="status">
            {resend.message}
          </p>
        )}
      </div>
    </section>
  );
}
